// The one place a slip's legs are read: which rows are places, where each leg
// starts, what the office table says about it, and what the submit function
// corrects. The slip editor (src/pages/PilotSlipEditor.jsx), submit_pilot_slip
// and scripts/base_time_audit.mjs all import this file, so they cannot
// disagree.
//
// Rules (Jill 2026-10-11, the approved plan "Pilot slip miles and base times"):
//   - Base time comes ONLY from the office table (segments.base_time_16 /
//     base_time_14). Nothing works out miles ÷ speed any more. A leg the table
//     has no time for has a blank base time.
//   - The portal no longer works out actual time or slow steaming; the office
//     and FileMaker do (decision 1). Those fields go out empty.
//   - The first real row after a Hubbard row is always the office's Hubbard
//     round trip: 62 nm, 3.8 h at 16 kt / 4.4 h at 14 kt, whatever names sit
//     on either side of Hubbard.
//   - Event rows (detention, sailing delay, shift, anchored, medivac and the
//     cargo events) and rate-event rows (Snow Passage, Hubbard) are never
//     places: they start no leg, end no leg and carry no miles.
//   - Where the table has a Snow Passage route for a pair, it is THE distance
//     (Jill 2026-07-25). Where it has several routes and no Snow Passage
//     route, the pilot picks one (decision 5); Frederick Sound PS to Juneau
//     starts on 95 (decision 6).
// Plain JavaScript. The only import is the shared event list, which Vite and
// Deno both load as is.
import { slipEventCode } from "./slipEvents.js";

const U = (s) => String(s ?? "").toUpperCase();
const round1 = (n) => (n == null || n === "" || isNaN(Number(n)) ? null : Math.round(Number(n) * 10) / 10);
const num = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
// Older phones stored 0 for "no base time" (miles ÷ speed of blank miles).
const baseOrNull = (v) => {
  const n = num(v);
  return n == null || n === 0 ? null : n;
};
const sameNum = (a, b) => (a == null && b == null) || (a != null && b != null && Math.abs(a - b) < 1e-9);

// ---- Rows -----------------------------------------------------------------

// Hubbard is IN Yakutat Bay: one charge and one code (HUB = "Hubbard, Yakutat
// Bay, Disenchantment Bay" on John's code list; Jill 2026-10-11). The bare
// codes count too (HUB, SNOW: the station list's codes), for a slip restored
// from an office JSON before the place-code lookup has renamed its rows
// (Codex on #580).
export function isHubbardRow(seg) {
  const n = U(seg?.location_name);
  return n.includes("HUBBARD") || n.includes("YAKUTAT BAY") || n.includes("DISENCHANTMENT BAY") || n.trim() === "HUB";
}
export function isSnowPassageRow(seg) {
  const n = U(seg?.location_name);
  return n.includes("SNOW PASSAGE") || n.trim() === "SNOW";
}
// A row that is never a place. Events are read by their event marker first
// (slipEventCode), so a reworded event row is still an event.
export function isNonPlaceRow(seg) {
  return !!slipEventCode(seg) || isSnowPassageRow(seg) || isHubbardRow(seg);
}

// ---- Legs -------------------------------------------------------------------

// One entry per row, in order. A place row gets the last place row before it
// (`from`, `prevIndex`) and whether a Hubbard row sits between the two
// (`afterHubbard`). A row with no name is not a place yet (a row just added,
// or a blank left on the slip): it is skipped, so the leg after it still runs
// from the last named place (ZAN26-VOY012's blank second line, 2026-10-11).
export function slipLegs(segments) {
  const list = Array.isArray(segments) ? segments : [];
  const out = [];
  let prevIndex = -1;
  let sawHubbard = false;
  list.forEach((s, i) => {
    if (isNonPlaceRow(s)) {
      if (isHubbardRow(s)) sawHubbard = true;
      out.push({ i, place: false });
      return;
    }
    const to = String(s?.location_name ?? "").trim();
    if (!to) {
      out.push({ i, place: false, nameless: true });
      return;
    }
    const from = prevIndex >= 0 ? String(list[prevIndex]?.location_name ?? "").trim() : null;
    out.push({ i, place: true, to, from, prevIndex, afterHubbard: sawHubbard });
    prevIndex = i;
    sawHubbard = false;
  });
  return out;
}

// The legs that carry miles, for callers that only need those
// ({ i, from, to, dist, afterHubbard }).
export function slipLegsForLookup(segments) {
  const list = Array.isArray(segments) ? segments : [];
  return slipLegs(list)
    .filter((l) => l.place && l.from && l.to && num(list[l.i]?.distance_nm) > 0)
    .map((l) => ({ i: l.i, from: l.from, to: l.to, dist: num(list[l.i].distance_nm), afterHubbard: l.afterHubbard }));
}

// ---- The office table ---------------------------------------------------------

const isSnowMarked = (r) => U(String(r?.snow_pass_hub ?? "").trim()) === "SNOW PASSAGE";
const isHubbardMarked = (r) => U(String(r?.snow_pass_hub ?? "").trim()) === "HUBBARD";
const printedOf = (r, speed) => {
  const v = Number(speed) === 14 ? r?.base_time_14 : r?.base_time_16;
  return v == null || v === "" ? null : Number(v);
};

// The office's Hubbard round trip: the HUBBARD-marked 62 nm row, which is
// "Yakutat P/S – HUB – Yakutat P/S, 62, 3.8 / 4.4" on the office sheet.
export function hubbardRoundTrip(rows, speed = 16) {
  const r = (Array.isArray(rows) ? rows : []).find(isHubbardMarked);
  if (!r) return null;
  return { distance: Number(r.distance_nm), baseTime: printedOf(r, speed) };
}

// Every route the table has for a pair, either direction, one per distance.
// When several rows share a distance (the Ward Cove spellings), the row that
// carries an office time wins, the way printed_base_time() in the database
// already chooses. The HUBBARD-marked rows are only ever reached through the
// Hubbard rule, never as an ordinary leg.
export function tableRoutes(rows, from, to) {
  const f = canonPlace(from);
  const t = canonPlace(to);
  if (!f || !t) return [];
  const hits = (Array.isArray(rows) ? rows : []).filter((r) => {
    if (isHubbardMarked(r)) return false;
    const a = canonPlace(r.start_location);
    const b = canonPlace(r.end_location);
    return (a === f && b === t) || (a === t && b === f);
  });
  // Deterministic order before merging, so the same table always gives the
  // same answer: Snow Passage rows first, then by spelling.
  hits.sort((x, y) =>
    Number(isSnowMarked(y)) - Number(isSnowMarked(x)) ||
    String(x.start_location).localeCompare(String(y.start_location)) ||
    String(x.end_location).localeCompare(String(y.end_location))
  );
  const byDist = new Map();
  for (const r of hits) {
    const d = Number(r.distance_nm);
    if (!Number.isFinite(d) || d <= 0) continue;
    const cur = byDist.get(d) || { distance: d, base16: null, base14: null, via: null, snow: false };
    byDist.set(d, {
      distance: d,
      base16: cur.base16 ?? printedOf(r, 16),
      base14: cur.base14 ?? printedOf(r, 14),
      via: cur.via || (r.via_secondary ? String(r.via_secondary) : null),
      snow: cur.snow || isSnowMarked(r),
    });
  }
  return [...byDist.values()].sort((x, y) => x.distance - y.distance);
}

// Two-route pairs that start on a route by Jill's ruling (2026-10-11,
// decision 6). Every other pair with more than one office route waits for
// the pilot to pick.
const DEFAULT_ROUTES = [
  { a: "frederick sound pilot station", b: "juneau", distance: 95 },
];
function defaultRouteFor(from, to, routes) {
  const f = canonPlace(from);
  const t = canonPlace(to);
  const hit = DEFAULT_ROUTES.find((d) => (d.a === f && d.b === t) || (d.a === t && d.b === f));
  return hit ? routes.find((r) => r.distance === hit.distance) || null : null;
}

// A route's name for a button, the way the office sheet names the water
// ("WRANGELL NARROWS" -> "Wrangell Narrows", "TRACY ARM Lay - To" -> "Tracy
// Arm Lay-To"). Never "Standard" (standing rule, Jill 2026-08-22).
export function routeLabel(route) {
  const via = String(route?.via ?? "")
    .replace(/\s*-\s*/g, "-")
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
    .trim();
  return via ? `${route.distance} via ${via}` : `${route.distance}`;
}

// What the office table says about one leg (an entry from slipLegs).
//   "hubbard"    first real row after Hubbard: 62 nm and the Hubbard time
//   "found"      one office distance (or the Snow Passage route)
//   "choose"     several office routes; `route` is the ruled default, if any
//   "not_found"  no office row for the pair: the pilot types the miles
//   "outside"    both ends are outside-water boundary points: no miles
//   "same_place" the same place twice: not a leg, no miles
//   "start"      the first place on the slip, or a nameless neighbour: no leg
//   "event"      not a place row
// `isBoundary(name)` is optional; the phone passes the station list's
// outside-water boundary points. Without it nothing is classed "outside".
export function legFromTable(rows, leg, { speed = 16, isBoundary = null } = {}) {
  if (!leg?.place) return { status: "event" };
  const pick = (r) => ({ distance: r.distance, baseTime: Number(speed) === 14 ? r.base14 : r.base16, via: r.via, label: routeLabel(r) });
  if (leg.afterHubbard) {
    const h = hubbardRoundTrip(rows, speed);
    return { status: "hubbard", distance: h ? h.distance : 62, baseTime: h ? h.baseTime : null, routes: [] };
  }
  if (!leg.from || !leg.to) return { status: "start", routes: [] };
  if (canonPlace(leg.from) === canonPlace(leg.to)) return { status: "same_place", routes: [] };
  const routes = tableRoutes(rows, leg.from, leg.to);
  if (!routes.length) {
    if (isBoundary && isBoundary(leg.from) && isBoundary(leg.to)) return { status: "outside", routes: [] };
    return { status: "not_found", routes: [] };
  }
  const snow = routes.find((r) => r.snow);
  if (snow) return { status: "found", ...pick(snow), routes: [pick(snow)] };
  if (routes.length === 1) return { status: "found", ...pick(routes[0]), routes: [pick(routes[0])] };
  const def = defaultRouteFor(leg.from, leg.to, routes);
  return { status: "choose", routes: routes.map(pick), route: def ? pick(def) : null };
}

// The office table's printed base time for a leg of `dist` miles, or null.
// Kept for the nightly check's simple callers; same selection as legFromTable.
export function printedBaseTimeFor(rows, from, to, dist, speed = 16) {
  const d = Number(dist);
  if (!Number.isFinite(d) || d <= 0) return null;
  const route = tableRoutes(rows, from, to).find((r) => r.distance === d);
  if (!route) return null;
  return Number(speed) === 14 ? route.base14 : route.base16;
}

// ---- The submit function's corrections ------------------------------------

// Decisions 2 and 3 (2026-10-11). For every leg: where the office table has
// one clear answer (one distance, the Snow Passage route, or the Hubbard round
// trip) its miles and time win. Where the pilot picked one of several office
// routes, that route's time; miles that match none of them are left over from
// an older build or a renamed line, and go out blank (the server never picks
// a route). A leg the table doesn't know keeps the pilot's miles, marked hand
// entered for the PDF, and has no base time. A line that is no leg (the first
// place, the same place twice, an event or rate-event line, a line with no
// name) carries no miles and no base time: the replay of the sent slips found
// those miles were always a neighbour's, counted twice (VVE26-VOY007's second
// Juneau, SOS26-VOY018's blank lines).
// Returns the corrected segments, one change record per row whose miles or
// base time changed, and the indexes of hand-entered rows.
export function gateSegments(segments, rows, { speed = 16 } = {}) {
  const list = Array.isArray(segments) ? segments.map((s) => ({ ...s })) : [];
  const changes = [];
  const handEntered = [];
  // Older phones stored 0 for blank miles too; 0 and blank are the same here.
  const milesOrNull = (v) => (v == null || v === 0 ? null : v);
  for (const leg of slipLegs(list)) {
    const s = list[leg.i];
    const distBefore = num(s.distance_nm);
    const baseBefore = baseOrNull(s.base_time_hours);
    let dist = distBefore;
    let base = null;
    let reason = "not a leg";
    if (leg.place) {
      const r = legFromTable(rows, leg, { speed });
      reason = r.status;
      if (r.status === "hubbard" || r.status === "found") {
        dist = r.distance;
        base = r.baseTime;
      } else if (r.status === "choose") {
        const m = r.routes.find((x) => x.distance === distBefore);
        if (m) {
          base = m.baseTime;
          reason = "route picked";
        } else {
          dist = null;
          reason = "route not picked";
        }
      } else if (r.status === "not_found") {
        if (distBefore > 0) {
          handEntered.push(leg.i);
          reason = "hand entered";
        }
      } else {
        dist = null; // "start" or "same_place"
      }
    } else {
      dist = null;
    }
    if (!sameNum(milesOrNull(dist), milesOrNull(distBefore)) || !sameNum(base, baseBefore)) {
      changes.push({
        index: leg.i,
        segment_number: s.segment_number ?? null,
        location_name: s.location_name ?? null,
        prev_place: leg.from ?? null,
        distance_before: distBefore,
        distance_after: dist,
        base_before: baseBefore,
        base_after: base,
        reason,
      });
    }
    s.distance_nm = dist;
    s.base_time_hours = base;
  }
  return { segments: list, changes, handEntered };
}

// The server's corrections ([{ index, distance_nm, base_time_hours }]) laid
// onto the phone's own copy of the segments before it saves them, so the
// database copy and the filed copy say what the PDF and JSON say. A key that
// is present is applied even when its value is null (a blanked base time).
export function applyServerCorrections(segments, corrections) {
  const list = Array.isArray(segments) ? segments : [];
  if (!Array.isArray(corrections) || !corrections.length) return list;
  const out = list.slice();
  for (const c of corrections) {
    const i = Number(c?.index);
    if (!Number.isInteger(i) || i < 0 || i >= out.length) continue;
    const patch = {};
    if (Object.prototype.hasOwnProperty.call(c, "distance_nm")) patch.distance_nm = c.distance_nm;
    if (Object.prototype.hasOwnProperty.call(c, "base_time_hours")) patch.base_time_hours = c.base_time_hours;
    out[i] = { ...out[i], ...patch };
  }
  return out;
}

// The old reply shape ([{ index, base_time_hours, slow_steaming_hours }]),
// kept so a reply from a server deployed before 2026-10-11 still applies.
export function applyServerBaseTimes(segments, baseTimes) {
  const list = Array.isArray(segments) ? segments : [];
  if (!Array.isArray(baseTimes) || !baseTimes.length) return list;
  const out = list.slice();
  for (const c of baseTimes) {
    const i = Number(c?.index);
    if (!Number.isInteger(i) || i < 0 || i >= out.length || c.base_time_hours == null) continue;
    out[i] = { ...out[i], base_time_hours: c.base_time_hours };
  }
  return out;
}

// ---- Numbers for the JSON and the PDF ----------------------------------------

// Rounds a slip's numbers. It no longer works anything out: no base time fill
// (office table only) and no actual time or slow steaming (decision 1); those
// two fields go out empty.
export function computeSlipSegments({ segments } = {}) {
  return (Array.isArray(segments) ? segments : []).map((seg) => {
    const s = { ...seg };
    if (s.distance_nm != null) s.distance_nm = round1(s.distance_nm);
    if (s.extra_miles != null && s.extra_miles !== "") s.extra_miles = round1(s.extra_miles);
    const base = baseOrNull(s.base_time_hours);
    s.base_time_hours = base == null ? null : round1(base);
    s.actual_time_hours = null;
    s.slow_steaming_hours = null;
    return s;
  });
}

// ---- Place names, the way the slip editor's distance lookup reads them ----
// normalizePlace + DISTANCE_NAME_ALIASES + canonPlace moved here from
// PilotSlipEditor.jsx on 2026-09-20 (unchanged), so the submit function and
// the nightly audit match a slip's place names against the segments table
// exactly the way the phone does. The database's normalize_location_name()
// is a different, narrower normaliser: it does not know these aliases.
// Full picker-vs-segments-table audit (Jill 2026-07-08): these picker names
// exist in the distance table under DIFFERENT spellings that no generic
// rule can bridge. Keys and values are post-normalize forms; the canonical
// side is the segments-table spelling.
export const DISTANCE_NAME_ALIASES = {
  "biorka is": "biorka",
  "frederick sd pilot station": "frederick sound pilot station",
  "fred snd pilot station": "frederick sound pilot station",
  "icy strait point hoonah": "icy strait point",
  "hoonah": "icy strait point",
  "misty fjord rudyerd bay": "misty fjord",
  "rudyerd bay": "misty fjord",
  "misty fjord walker cove": "walker cove",
  "sitka eastern anchorage": "sitka east anchorage",
  // Older names still on 2026 slips, matched the way the place-code table
  // (pilot_slip_location_aliases) already reads them: Ocean Cape Pilot Station
  // is the retired name of Yakutat Pilot Station (Jill, summer 2026), "Rocks"
  // is the old spelling of Bieli Rks, PTB is the calendar code for Petersburg,
  // and the builder's old "verify berth" flags for the Sitka cruise terminal
  // (STI, STO, ISD, OSD all mean Sitka, Jill 2026-10-11).
  "ocean cape pilot station": "yakutat pilot station",
  "bieli rocks pilot station": "bieli rks pilot station",
  "ptb": "petersburg",
  "sitka — verify berth (sti)": "sitka",
  "sitka — verify berth (sto)": "sitka",
  "sitka — verify berth (isd)": "sitka",
  "sitka — verify berth (osd)": "sitka",
  // Small-ship route patterns (Star Seeker) spelled two Sitka berths their
  // own way; the patterns are renamed to the station list's names on
  // 2026-10-11, and these keep drafts built from the old spellings matching.
  "sitka (e anchorage)": "sitka east anchorage",
  "sitka (osd)": "sitka",
  // The Biorka/Cape Edgecumbe distances are filed under "SITKA CT" (cruise
  // terminal) in the segments table while the slip picker says "Sitka" —
  // fold CT into plain Sitka so those pairs match (Chris, 2026-07-20).
  // Safe: no pair exists under BOTH spellings, so nothing double-matches.
  "sitka ct": "sitka",
  // The distance table's FULL Ward Cove set (incl. every Snow Passage-marked
  // long haul: JUNEAU 284, SKAGWAY 340, Pt Alexander 87…) lives under
  // "KETCHIKAN WW"; only the two short Guard Is/Twin Is pairs have "WARD
  // COVE" twins. Folding to "ward cove" made every long leg unfindable the
  // moment the row got its canonical "Ward Cove Ketchikan" name (found
  // 2026-07-30 on Jill's SSE slip) — fold to the spelling that has all rows.
  "ward cove ketchikan": "ketchikan ww",
  "ward cove": "ketchikan ww",
  "yakutat": "monti bay", // Yakutat's dock is in Monti Bay — the table's rows live under that name
};

// Identical to the slip editor's fetchDistance normalisation.
export function normalizePlace(name) {
  return (
    name
      ?.trim()
      .toLowerCase()
      .replace(/[.,'’]/g, " ") // "Twin Is. PS" vs "Twin Is Pilot Station"
      .replace(/\b(ps|pilot station)\b/g, "pilot station")
      .replace(/\b(pt|point)\b/g, "point")
      .replace(/\bislands?\b/g, "is")
      .replace(/\bfjords\b/g, "fjord")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\bpilot\b(?! station)/g, "pilot station")
    ?? ""
  );
}
export function canonPlace(name) {
  const n = normalizePlace(name);
  return DISTANCE_NAME_ALIASES[n] || n;
}
